import {
  Button,
  ButtonDropdown,
  ButtonDropdownProps,
  CollectionPreferences,
  FormField,
  Header,
  Input,
  Link,
  Pagination,
  Select,
  SelectProps,
  SpaceBetween,
  StatusIndicator,
  Table,
  TableProps,
} from "@amzn/awsui-components-console";
import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAdminCachedMessageActions } from "@/hooks/admin/useAdminCachedMessageActions";
import { useAdminCachedMessages } from "@/hooks/admin/useAdminCachedMessages";
import useAdminGetUsers from "@/hooks/admin/useAdminGetUsers";
import {
  ADMIN_DEFAULT_PAGE_SIZE,
  ADMIN_TABLE_PREFERENCES,
  adminPageSizePreference,
} from "@/utils/adminTable";
import { ULID_COLUMN_WIDTH_PX } from "@/utils/identifiers";
import { formatTimestamp } from "@/utils/utils";

import CacheButton from "../buttons/CacheButton";
import { CACHE_ACTION_ICONS } from "../buttons/config";
import { TablePreferences } from "../chat/types";
import LastChangedCell from "../common/LastChangedCell";
import EmptyState from "../EmptyState";
import LoadingSpinner from "../LoadingSpinner";
import {
  CachedMessagesSortKey,
  CacheStatus,
  MessageItem,
  UpdateCacheAction,
} from "./types";

const CachedMessages: React.FC = () => {
  const navigate = useNavigate();
  const [selectedItems, setSelectedItems] = useState<MessageItem[]>([]);
  const [senderFilter, setSenderFilter] =
    useState<SelectProps.Option | null>(null);
  const [statusFilter, setStatusFilter] =
    useState<SelectProps.Option | null>(null);
  const [searchText, setSearchText] = useState("");
  const { loading: usersLoading, items: users } = useAdminGetUsers();

  // The admin page size, not the shared one the chat and cart clip tables use:
  // this is the number the first request asks the endpoint for, so the gear's
  // selected default and the page size actually requested stay the same value.
  const [preferences, setPreferences] =
    useState<TablePreferences>(ADMIN_TABLE_PREFERENCES);
  const {
    loading,
    items,
    queryParams,
    pagination,
    updateQueryParams,
    fetchCachedMessages,
    applyCachedState,
  } = useAdminCachedMessages({
    pageSize: preferences.pageSize,
  });

  /**
   * Reflect one settled row without rebuilding the list.
   *
   * `applyCachedState` is stable for the hook's lifetime, so this is too, which is
   * what keeps it out of the way of the `columnDefinitions` memo below.
   */
  const handleRowSettled = useCallback(
    (id: string, cached: boolean) => applyCachedState([id], cached),
    [applyCachedState]
  );
  const columnDefinitions = useMemo<TableProps["columnDefinitions"]>(
    () => [
      {
        id: "id",
        header: "Message ID",
        // Wide enough for a full ULID, and `minWidth` so a resize or a flex
        // squeeze cannot take it below that: a clipped id can be neither copied
        // nor compared, which is the whole reason the column is here.
        width: ULID_COLUMN_WIDTH_PX,
        minWidth: ULID_COLUMN_WIDTH_PX,
        cell: ({ id }: MessageItem) => (
          // `nowrap` is what keeps the id on one unbroken line, and it holds even
          // if the width estimate is off. This table sets `wrapLines`, so the
          // guarantee has to be stated on the cell rather than left to the column
          // being wide enough. overflow + textOverflow are the fallback for
          // content wider than the column: nowrap alone would let it spill past
          // the boundary, the two together clip it with an ellipsis instead.
          <span
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <Link onClick={() => navigate(`/admin/cached-messages/${id}`)}>
              {id}
            </Link>
          </span>
        ),
        isRowHeader: true,
      },
      {
        // Keyed `senderName`, not `userId`: this list is sorted server-side, and
        // the endpoint orders it by the display name resolved below rather than
        // by the id the record stores. The `id` has to match the key too, because
        // the table looks its sorted column up by matching `queryParams.sortBy`
        // against a column id.
        id: "senderName",
        header: "Original Sender",
        cell: ({ userId }: MessageItem) =>
          usersLoading ? (
            <LoadingSpinner size="normal" />
          ) : (
            users?.find((user) => user.userId === userId)?.name ??
            `User #${userId}`
          ),
        sortingField: "senderName",
      },
      {
        id: "created",
        header: "Created",
        cell: ({ created }: MessageItem) => formatTimestamp(created),
        sortingField: "created",
      },
      {
        id: "lastUpdated",
        // The editing admin goes above this column's timestamp rather than in a
        // column of its own. The identity is rendered exactly as the list
        // endpoint returned it -- it is not resolved through the users list,
        // which answers a different question (see "Original Sender" above).
        // `sortingField` stays the timestamp, so sorting this column remains a
        // server-side sort on `lastUpdated` and the identity cannot reorder rows.
        header: "Last Updated",
        cell: ({ id, lastUpdated, lastChangedBy }: MessageItem) => (
          <LastChangedCell
            identity={lastChangedBy}
            timestamp={lastUpdated}
            testId={id}
          />
        ),
        sortingField: "lastUpdated",
      },
      {
        id: "cacheStatus",
        header: "Status",
        cell: ({ cached, cacheStatus }: MessageItem) => (
          <StatusIndicator type={cached ? "success" : "stopped"}>
            {cacheStatus}
          </StatusIndicator>
        ),
        sortingField: "cacheStatus",
      },
      {
        id: "actions",
        header: "Action",
        cell: (message: MessageItem) => (
          <CacheButton message={message} onSettled={handleRowSettled} />
        ),
      },
    ],
    // Deliberately not `fetchCachedMessages`: its identity changes with every
    // query-param change, which rebuilt this array and re-rendered every cell in
    // the table. `handleRowSettled` is stable, so the columns are too.
    [usersLoading, users, handleRowSettled]
  );


  const handleRefresh = () => {
    setSelectedItems([]);
    fetchCachedMessages();
  };

  const {
    loading: actionLoading,
    cacheMessages,
    uncacheMessages,
  } = useAdminCachedMessageActions();

  const handleBulkAction = async ({
    detail: { id },
  }: CustomEvent<ButtonDropdownProps.ItemClickDetails>) => {
    const messageIds: string[] = selectedItems.map(({ id }) => id);
    let settled: string[] = [];

    switch (id) {
      case UpdateCacheAction.CACHE:
        ({ settled } = await cacheMessages(messageIds));
        applyCachedState(settled, true);
        break;
      case UpdateCacheAction.UNCACHE:
        ({ settled } = await uncacheMessages(messageIds));
        applyCachedState(settled, false);
        break;
    }

    // Only the rows that actually changed are updated -- no list refetch, which
    // on this table is a full DynamoDB scan. Selection is cleared because the
    // selected objects are now stale copies, and the bulk menu's enablement reads
    // `cached` off them.
    setSelectedItems([]);
  };

  return (
    <>
      <Table
        onSelectionChange={({ detail }) =>
          setSelectedItems(detail.selectedItems)
        }
        selectionType="multi"
        selectedItems={selectedItems}
        renderAriaLive={({ firstIndex, lastIndex, totalItemsCount }) =>
          `Displaying items ${firstIndex} to ${lastIndex} of ${totalItemsCount}`
        }
        wrapLines
        // Rows are keyed on the message id, not on their position. Without this
        // the table tracks selection by object reference, and a sort or page
        // change refetches, so the new items match nothing: the checkbox
        // disappears while `selectedItems` still holds the stale copies, and a
        // bulk action would then fire against a row showing no checkbox.
        trackBy="id"
        onSortingChange={({ detail }) => {
          setSelectedItems([]);
          updateQueryParams({
            page: 1,
            sortBy: detail.sortingColumn.sortingField as CachedMessagesSortKey,
            sortOrder: detail.isDescending ? "desc" : "asc",
          });
        }}
        sortingColumn={columnDefinitions.find(
          ({ id }) => id === queryParams.sortBy
        )}
        sortingDescending={queryParams.sortOrder === "desc"}
        columnDefinitions={columnDefinitions}
        enableKeyboardNavigation
        loading={loading}
        items={items}
        header={
          <Header
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  variant="icon"
                  iconName="refresh"
                  onClick={handleRefresh}
                  disabled={loading || actionLoading}
                />
                <ButtonDropdown
                  loading={actionLoading}
                  disabled={loading || actionLoading}
                  items={[
                    {
                      text: "Cache",
                      id: UpdateCacheAction.CACHE,
                      iconName: CACHE_ACTION_ICONS[UpdateCacheAction.UNCACHE],
                      disabledReason: "Must select only entries to cache.",
                      disabled:
                        selectedItems.length <= 0 ||
                        selectedItems.some(({ cached }) => cached),
                    },
                    {
                      text: "Uncache",
                      id: UpdateCacheAction.UNCACHE,
                      iconName: CACHE_ACTION_ICONS[UpdateCacheAction.UNCACHE],
                      disabledReason: "Must select only entries to uncache.",
                      disabled:
                        selectedItems.length <= 0 ||
                        selectedItems.some(({ cached }) => !cached),
                    },
                  ]}
                  onItemClick={handleBulkAction}
                >
                  Bulk Actions
                </ButtonDropdown>
              </SpaceBetween>
            }
            counter={`(${pagination?.totalItems ?? 0})`}
          >
            Messages
          </Header>
        }
        filter={
          <SpaceBetween direction="horizontal" size="xs">
            <FormField label="Search">
              <Input
                value={searchText}
                placeholder="Search messages..."
                onChange={({ detail }) => {
                  setSearchText(detail.value);
                  if (!detail.value) {
                    updateQueryParams({ page: 1, search: undefined });
                  }
                }}
                onKeyDown={({ detail }) => {
                  if (detail.key === "Enter") {
                    updateQueryParams({ page: 1, search: searchText || undefined });
                  }
                }}
                type="search"
              />
            </FormField>
            <FormField label="Sender">
              <Select
                selectedOption={senderFilter}
                onChange={({ detail }) => {
                  setSenderFilter(detail.selectedOption);
                  updateQueryParams({
                    page: 1,
                    userId: detail.selectedOption?.value || undefined,
                  });
                }}
                options={[
                  { label: "All senders", value: "" },
                  ...(users?.map((user) => ({
                    label: user.name || `User #${user.userId}`,
                    value: user.userId,
                  })) ?? []),
                ]}
                placeholder="All senders"
              />
            </FormField>
            <FormField label="Status">
              <Select
                selectedOption={statusFilter}
                onChange={({ detail }) => {
                  setStatusFilter(detail.selectedOption);
                  updateQueryParams({
                    page: 1,
                    cacheStatus:
                      (detail.selectedOption?.value as CacheStatus) || undefined,
                  });
                }}
                options={[
                  { label: "All statuses", value: "" },
                  { label: "Active (Cached)", value: CacheStatus.ACTIVE },
                  { label: "Inactive (Uncached)", value: CacheStatus.INACTIVE },
                ]}
                placeholder="All statuses"
              />
            </FormField>
            {(senderFilter?.value || statusFilter?.value || searchText) && (
              <FormField label="&nbsp;">
                <Button
                  variant="link"
                  onClick={() => {
                    setSenderFilter(null);
                    setStatusFilter(null);
                    setSearchText("");
                    updateQueryParams({
                      page: 1,
                      userId: undefined,
                      cacheStatus: undefined,
                      search: undefined,
                    });
                  }}
                >
                  Clear filters
                </Button>
              </FormField>
            )}
          </SpaceBetween>
        }
        pagination={
          <Pagination
            currentPageIndex={queryParams.page}
            pagesCount={pagination?.totalPages ?? 1}
            onChange={({ detail }) => {
              setSelectedItems([]);
              updateQueryParams({ page: detail.currentPageIndex });
            }}
          />
        }
        stripedRows={preferences.stripedRows}
        preferences={
          <CollectionPreferences
            title="Preferences"
            confirmLabel="Confirm"
            cancelLabel="Cancel"
            preferences={preferences}
            pageSizePreference={adminPageSizePreference()}
            stripedRowsPreference={{
              label: "Striped Rows",
              description: "Select to add alternating shaded rows",
            }}
            onConfirm={({ detail }) =>
              setPreferences({
                ...(detail as TablePreferences),
                pageSize: detail.pageSize ?? ADMIN_DEFAULT_PAGE_SIZE,
              })
            }
          />
        }
        empty={<EmptyState title="No messages found." />}
      />
    </>
  );
};

export default CachedMessages;
